import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { skipToken } from '@reduxjs/toolkit/query';
import moment from 'moment';
import { type CBNavItem } from '@chargebee/cb-navbar';
import { useHistory, useLocation } from '@bbkShared/react-router';
import { useCreatePagesListUrl, useUrl } from '@bbkAdminUtils/nav';
import InternalReactPages from '@bbkAdminComponents/internal/internal-pages';
import { selectApp, selectAppId } from '@bbkAdminRedux/app/selectors';
import { useGetProductLicense, usePlans } from '@bbkAdminUtils/plans';
import { useStrings } from '@bbkAdminUtils/strings';
import { AuthorizedPaths } from '@bbkAdminRouter/router-paths';
import {
  useDaysToExpiration,
  usePlanPurchaseIntent,
} from '@bbkAdminRedux/rtkq/checkout.slice';
import { FeatureFlagsE, useFeatureFlags } from '@bbkAdminUtils/feature-flags';
import {
  NavIcons,
  usePurchaseFeature,
} from '@bbkAdminComponents/app-layout/nav';
import { useSelectBillingIntegration } from '@bbkAdminRedux/rtkq/integrations.slice';
import { billingConfigSlice } from '@bbkAdminRedux/rtkq/billing-config.slice';
import { GetStartedNav } from '@bbkAdminComponents/get-started/get-started-nav';
import { useSelectCurrentApp } from '@bbkAdminRedux/rtkq/current-user.slice';
import { useGetStartedStatus } from '@bbkAdminComponents/get-started/get-started-page';
import { useQueryFeatureFlags } from '@bbkAdminRedux/rtkq/feature-flags.slice';
import {
  ONBOARDING_HISTORY_STEP,
  onboardingHistorySlice,
  selectOnboardingHistoryMap,
} from '@bbkAdminRedux/rtkq/onboarding-history.slice';
import { determineSelectedNavItem } from '@bbkAdminRedux/app/reducers';
import icons from '@bbkAdminUtils/icons';
import { badgesSlice } from '@bbkAdminRedux/rtkq/badges.slice';
import { Badge } from '@bbkAdminComponents/badges/badge';
import { type BBKNavbarOpt } from '@bbkAdminComponents/app-layout/nav/bbk-navbar-opt';
import { appNotPersistedSlice } from '@bbkAdminRedux/rtkq/app-not-persisted.slice';
import { useAppDispatch } from '@bbkAdminRedux/redux_utils';
import { Constants } from '@bbkShared/utils/constants/ui-constants';
import { useRoles } from '@bbkAdminUtils/roles';
import { isDefined } from '@bbkAdminRedux/rtkq/rtkq-utils';
import { NavBarHelper } from '@bbkAdminComponents/app-layout/nav/NavBarHelper'; // Helper function to filter cbNavbarOptions by show property

export const useNavbarConfig = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const history = useHistory();
  const { data: featureFlags } = useQueryFeatureFlags();
  const app = useSelector(selectApp);
  const currentApp = useSelectCurrentApp();

  const selectedNavItem = determineSelectedNavItem(location.pathname);
  const createUrl = useUrl();

  const expiration = useDaysToExpiration();

  const [trendsExpanded, setTrendsExpanded] = useState(false);
  const [customersExpanded, setCustomersExpanded] = useState(false);
  const [internalExpanded, setInternalExpanded] = useState(false);
  const [experiencesExpanded, setExperiencesExpanded] = useState(false);
  const [insightsExpanded, setInsightsExpanded] = useState(false);
  const [reportsExpanded, setReportsExpanded] = useState(false);

  const appKey = useSelector(selectAppId);
  const billingIntegration = useSelectBillingIntegration({ appKey });

  const productLicense = useGetProductLicense();
  const agsLicense = productLicense.agsLicense;
  const deflectLicense = productLicense.deflectLicense;
  const pricingPagesEnabled = useFeatureFlags(
    FeatureFlagsE.ENABLE_PRICING_TABLE
  );

  const { hasBillingRole } = useRoles();
  const { alertText } = usePurchaseFeature();
  const handlePlanPurchaseIntent = usePlanPurchaseIntent();

  const { data: billingConfig } =
    billingConfigSlice.endpoints.getBillingConfig.useQuery(
      billingIntegration ? { app_key: appKey } : skipToken
    );
  const isRepurchaseAttributionEnabled =
    billingConfig?.repurchase_based_attribution;

  const { data: steps = {} } =
    onboardingHistorySlice.endpoints.getOnboardingHistorySteps.useQuery(
      { appKey },
      {
        skip: appKey === '_' || app?.get_started_status !== 'ELIGIBLE',
        selectFromResult: selectOnboardingHistoryMap,
      }
    );

  const { data: badges = [] } = badgesSlice.endpoints.getBadges.useQuery(
    !appKey || appKey === '_' ? skipToken : appKey,
    { pollingInterval: 60000 }
  );

  const showSettingsBadge = badges.some(
    (b) => b.name === 'application_log' && b.state === 'flag'
  );
  const settingsMenuBadge = showSettingsBadge ? (
    <span className="tw-ml-2">
      <Badge badgeClasses="bbk-badge-flat bbk-badge-stat notify" />
    </span>
  ) : undefined;

  const isGetStartedEligible = currentApp?.get_started_status === 'ELIGIBLE';
  const { isTestAndLaunchCompleted, isCreateExperienceCompleted } =
    useGetStartedStatus();
  const showGetStarted = isGetStartedEligible;
  const isCreateOrLaunchCompleted =
    isCreateExperienceCompleted || isTestAndLaunchCompleted;
  const isExpLibraryDisabled = showGetStarted && !isCreateOrLaunchCompleted;
  const isCreateOrLaunchCompletedInPast =
    steps[ONBOARDING_HISTORY_STEP.BRANDING] ||
    steps[ONBOARDING_HISTORY_STEP.GO_LIVE];
  const isExpBrandingLibDisabled = isExpLibraryDisabled
    ? showGetStarted && !isCreateOrLaunchCompletedInPast
    : isExpLibraryDisabled;
  const pagesListUrl = useCreatePagesListUrl(
    !isCreateOrLaunchCompleted && isCreateOrLaunchCompletedInPast
  );

  const additionalBrandsEnabled = useFeatureFlags(
    FeatureFlagsE.ADDITIONAL_BRANDS_ENABLED
  );
  const isCancelInsightsAIEnabled = useFeatureFlags(
    FeatureFlagsE.ENABLE_LLM_FEEDBACK_CLASSIFIER
  );
  const showPricingOptimizer = useFeatureFlags(
    FeatureFlagsE.SHOW_PRICE_OPTIMIZER_PAGE
  );

  // Plan filters
  const { isStarterFreePlan, isStarterPremiumPlan } = usePlans();
  // show welcome popup once per user for trial/starter
  const isStarter = isStarterFreePlan || isStarterPremiumPlan;

  const strings = useStrings();

  const regexTest = useCallback(
    (regex: string | RegExp): boolean => {
      // test is general, regex is the string to test for
      // i.e. trendsReport, trends => true
      const routeRegex = new RegExp(regex);
      return routeRegex.test(selectedNavItem);
    },
    [selectedNavItem]
  );

  useEffect(() => {
    if (agsLicense && !deflectLicense) {
      if (
        regexTest(AuthorizedPaths.dashboard) &&
        !regexTest(AuthorizedPaths.viewPredictiveCustomerPreview)
      ) {
        history.push(createUrl(AuthorizedPaths.plays));
      }
    }
  }, [
    agsLicense,
    createUrl,
    deflectLicense,
    history,
    location.pathname,
    regexTest,
  ]);

  useEffect(() => {
    setTrendsExpanded(regexTest('trends'));
    setCustomersExpanded(regexTest('customers'));
    setInternalExpanded(regexTest('internal'));
    setInsightsExpanded(regexTest('insights'));
    if (agsLicense) {
      setExperiencesExpanded(!regexTest('offers') && regexTest('experiences'));
    } else {
      setExperiencesExpanded(regexTest('experiences'));
    }
    setReportsExpanded(
      regexTest('trends') || regexTest('insights') || regexTest('customers')
    );
  }, [agsLicense, regexTest]);

  const showInternal = featureFlags
    ? InternalReactPages.some((p) =>
        p.featureFlags.every((ff) => featureFlags.includes(ff))
      )
    : false;

  const expired = expiration
    ? expiration.endDate.diff(moment.utc(), 'days') < 0
    : false;

  const internalNavbarOptions = [
    {
      id: 'admin',
      leadingIcon: NavIcons.admin,
      label: 'Admin',
      linkTo: '/internal/companies',
      onClick: (e) => {
        const newVal = !internalExpanded;
        setInternalExpanded(newVal);
        if (!newVal) e.preventDefault();
      },
      highlightBg: false,
      show: showInternal,
      expandChildren: internalExpanded,
      subItems: InternalReactPages.filter(
        (x) =>
          !x.hiddenFromNav &&
          x.featureFlags.every((ff) => featureFlags.includes(ff))
      ).map((x) => ({
        id: x.path,
        linkTo: x.path,
        label: x.title,
      })),
    },
  ] satisfies BBKNavbarOpt[];

  // AGS SideBar code start

  const getStartedNavOpt = {
    id: 'get started',
    label: (<GetStartedNav />) as unknown as string,
    linkTo: createUrl(AuthorizedPaths.getStarted),
    show: deflectLicense && showGetStarted,
  } satisfies BBKNavbarOpt;
  const dashboardNavOpt = {
    label: 'Dashboard',
    leadingIcon: NavIcons.dashboard,
    linkTo: createUrl(AuthorizedPaths.dashboard),
    id: 'dashboard',
    show: deflectLicense,
  } satisfies BBKNavbarOpt;
  const playsNavOpt = {
    label: 'Plays',
    leadingIcon: icons.play,
    linkTo: createUrl(AuthorizedPaths.plays),
    id: 'plays',
    show: agsLicense,
  } satisfies BBKNavbarOpt;
  const offerGroupNavOpt = {
    label: 'Offers',
    id: 'offers-group',
  } satisfies BBKNavbarOpt;
  const offerNavOpt = {
    label: 'Offers',
    tooltip: isExpBrandingLibDisabled
      ? 'Create your first cancel experience on the get started page to view this section.'
      : isStarter
        ? strings.UPGRADE_PLAN({
            description: strings.UPGRADE_TO_DEFLECTION_DESC,
          })
        : undefined,
    leadingIcon: NavIcons.loyalty,
    trailingIcon: isStarter ? NavIcons.lock : undefined,
    linkTo: createUrl(AuthorizedPaths.offers),
    id: 'offers',
    disabled: isExpBrandingLibDisabled || isStarter,
  } satisfies BBKNavbarOpt;
  const brandingNavOpt = {
    id: 'branding',
    label: strings.BRANDING_DISPLAY_NAME,
    linkTo: createUrl(AuthorizedPaths.experiencesBrandingPage),
    leadingIcon: NavIcons.photograph,
    show: !additionalBrandsEnabled,
    disabled: isExpBrandingLibDisabled,
  } satisfies BBKNavbarOpt;
  const brandsNavOpt = {
    label: strings.BRANDS_DISPLAY_NAME,
    linkTo: createUrl(AuthorizedPaths.additionalBrandsPage),
    leadingIcon: NavIcons.photograph,
    disabled: isExpBrandingLibDisabled,
    show: additionalBrandsEnabled,
    id: 'brands',
  } satisfies BBKNavbarOpt;
  const pagesNavOpt = {
    label: 'Pages',
    id: 'pages',
    linkTo: pagesListUrl,
    leadingIcon: NavIcons.duplicateDocument,
    // @ts-ignore
    extraLinks: [createUrl('/experiences/funnels')],
    disabled: isExpBrandingLibDisabled || (agsLicense && !deflectLicense),
    show: deflectLicense,
  } satisfies BBKNavbarOpt;
  const pricingPagesNavOpt = {
    label: 'Pricing pages',
    id: 'pricing-pages',
    linkTo: createUrl(AuthorizedPaths.experiencesPricingPages),
    leadingIcon: NavIcons.duplicateDocument,
    show: pricingPagesEnabled,
  } satisfies BBKNavbarOpt;
  const targetingNavOpt = {
    tooltip: isStarter
      ? strings.UPGRADE_PLAN({
          description: strings.UPGRADE_TO_DEFLECTION_DESC,
        })
      : undefined,
    label: 'Targeting',
    leadingIcon: NavIcons.target,
    trailingIcon: isStarter || expired ? NavIcons.lock : undefined,
    linkTo: createUrl(AuthorizedPaths.experiencesTargeting),
    id: 'targeting',
    show: deflectLicense,
    disabled: isStarter || isExpLibraryDisabled || isExpBrandingLibDisabled,
  } satisfies BBKNavbarOpt;
  const laNavOpt = {
    tooltip: isStarter
      ? strings.UPGRADE_PLAN({
          description: strings.UPGRADE_TO_DEFLECTION_DESC,
        })
      : undefined,
    label: 'Loss aversion cards',
    leadingIcon: NavIcons.problem,
    trailingIcon: isStarter ? NavIcons.lock : undefined,
    disabled: isStarter || isExpLibraryDisabled,
    linkTo: createUrl(AuthorizedPaths.lossAversionList),
    show: deflectLicense,
    id: 'loss aversion cards',
  } satisfies BBKNavbarOpt;
  const surveyReasonNavOpt = {
    label: 'Survey reasons',
    leadingIcon: NavIcons.server,
    disabled: isExpBrandingLibDisabled,
    linkTo: createUrl(AuthorizedPaths.experiencesPageReasons),
    show: deflectLicense,
    id: 'survey reasons',
  } satisfies BBKNavbarOpt;
  const redirectPagesNavOpt = {
    label: 'Redirect pages',
    leadingIcon: NavIcons.checkCircle,
    linkTo: createUrl(AuthorizedPaths.confirmationPages),
    disabled: isExpBrandingLibDisabled,
    id: 'redirect pages',
  } satisfies BBKNavbarOpt;
  const pricingOptimizerNavOpt = {
    label: 'Pricing Actions',
    linkTo: createUrl(AuthorizedPaths.pricingOptimizations),
    id: 'pricing optimizer',
    leadingIcon: NavIcons.pricingActions,
    show: showPricingOptimizer,
  } satisfies BBKNavbarOpt;
  const peopleNavOpt = {
    label: 'People',
    id: 'people',
  } satisfies BBKNavbarOpt;
  const audiencesNavOpt = {
    tooltip: isExpBrandingLibDisabled
      ? 'Create your first cancel experience on the get started page to view this section.'
      : isStarter
        ? strings.UPGRADE_PLAN({
            description: strings.UPGRADE_TO_DEFLECTION_DESC,
          })
        : undefined,
    label: 'Audiences',
    leadingIcon: NavIcons.audiences,
    trailingIcon: isStarter || expired ? NavIcons.lock : undefined,
    disabled: isStarter || isExpBrandingLibDisabled,
    linkTo: createUrl(AuthorizedPaths.audiencesList),
    id: 'audiences',
  } satisfies BBKNavbarOpt;
  const reportsNavOpt = {
    label: 'Reports',
    leadingIcon: NavIcons.barChart,
    highlightBg: false,
    linkTo: isStarter
      ? createUrl(AuthorizedPaths.insightsCancel)
      : createUrl(AuthorizedPaths.insightsExperiencePerformanceReport),
    onClick: (e) => {
      const nextReportsState = !reportsExpanded;
      setReportsExpanded(nextReportsState);
      const nextInsightState = !insightsExpanded;
      setInsightsExpanded(nextInsightState);
      if (!(nextReportsState || nextInsightState)) e.preventDefault();
    },
    id: 'reports',
    show: deflectLicense,
    expandChildren: reportsExpanded,
  } satisfies BBKNavbarOpt;
  const insightsNavOpt = {
    label: 'Insights',
    highlightBg: false,
    leadingIcon: NavIcons.lightBulb,
    trailingIcon: expired ? NavIcons.lock : undefined,
    show: deflectLicense,
    id: 'insights',
    linkTo: isStarter
      ? createUrl(AuthorizedPaths.insightsCancel)
      : createUrl(AuthorizedPaths.insightsExperiencePerformanceReport),
    onClick: (e) => {
      const newVal = !insightsExpanded;
      setInsightsExpanded(newVal);
      if (!newVal) e.preventDefault();
    },
    expandChildren: insightsExpanded,
  } satisfies BBKNavbarOpt;
  const insightsCancelAIReportNavOpt = {
    label: 'Cancels (AI)',
    linkTo: createUrl(AuthorizedPaths.insightsCancelAI),
    show: isCancelInsightsAIEnabled,
    id: 'insights cancel_ai',
  } satisfies BBKNavbarOpt;
  const insightsCancelsNavOpt = {
    label: 'Cancels',
    linkTo: createUrl(AuthorizedPaths.insightsCancel),
    id: 'insights cancels',
    show: deflectLicense,
  } satisfies BBKNavbarOpt;
  const insightsOfferPerfNavOpt = {
    tooltip: isStarter
      ? strings.UPGRADE_PLAN({
          description: strings.UPGRADE_TO_DEFLECTION_DESC,
        })
      : undefined,
    label: 'Offer Performance',
    trailingIcon: isStarter ? NavIcons.lock : undefined,
    linkTo: createUrl(AuthorizedPaths.insightsOfferPerformance),
    disabled: isStarter,
    id: 'insights offer performance',
    show: deflectLicense,
  } satisfies BBKNavbarOpt;
  const insightsExpPerfNavOpt = {
    tooltip: isStarter
      ? strings.UPGRADE_PLAN({
          description: strings.UPGRADE_TO_DEFLECTION_DESC,
        })
      : undefined,
    label: 'Experience Performance',
    trailingIcon: isStarter ? NavIcons.lock : undefined,
    linkTo: createUrl(AuthorizedPaths.insightsExperiencePerformanceReport),
    disabled: isStarter,
    show: deflectLicense,
    id: 'insights experience performance',
  } satisfies BBKNavbarOpt;
  const insightsRetainRevenueNavOpt = {
    tooltip: isStarter
      ? strings.UPGRADE_PLAN({
          description: strings.UPGRADE_TO_DEFLECTION_DESC,
        })
      : undefined,
    label: 'Retained Revenue Cohort',
    trailingIcon: isStarter ? NavIcons.lock : undefined,
    linkTo: createUrl(AuthorizedPaths.insightsRetainedRevenueCohort),
    disabled: isStarter,
    show: deflectLicense && Boolean(isRepurchaseAttributionEnabled),
    id: 'insights retained revenue cohort',
  } satisfies BBKNavbarOpt;
  const insightsLiftReportNavOpt = {
    tooltip: isStarter
      ? strings.UPGRADE_PLAN({
          description: strings.UPGRADE_TO_DEFLECTION_DESC,
        })
      : undefined,
    label: 'Lift Report',
    trailingIcon: isStarter ? NavIcons.lock : undefined,
    linkTo: createUrl(AuthorizedPaths.insightsLiftDashboard),
    disabled: isStarter,
    show: deflectLicense,
    id: 'insights lift report',
  } satisfies BBKNavbarOpt;
  const trendsNavOpt = {
    label: 'Trends',
    highlightBg: false,
    leadingIcon: NavIcons.trendingUp,
    trailingIcon: expired ? NavIcons.lock : undefined,
    linkTo: createUrl(AuthorizedPaths.trendsSavePerformance),
    onClick: (e) => {
      const nextTrendsState = !trendsExpanded;
      setTrendsExpanded(nextTrendsState);
      if (!nextTrendsState) e.preventDefault();
    },
    show: deflectLicense,
    expandChildren: trendsExpanded,
    id: 'trends',
  } satisfies BBKNavbarOpt;
  const trendsSavePerfNavOpt = {
    label: 'Save performance',
    linkTo: createUrl(AuthorizedPaths.trendsSavePerformance),
    id: 'trends save performance',
    show: deflectLicense,
  } satisfies BBKNavbarOpt;
  const trendsDeflectsNavOpt = {
    tooltip: isStarter
      ? strings.UPGRADE_PLAN({
          description: strings.UPGRADE_TO_DEFLECTION_DESC,
        })
      : undefined,
    label: 'Deflection',
    trailingIcon: isStarter ? NavIcons.lock : undefined,
    linkTo: createUrl(AuthorizedPaths.trendsDeflection),
    disabled: isStarter,
    id: 'trends deflection',
    show: deflectLicense,
  } satisfies BBKNavbarOpt;
  const trendsCancelsNavOpt = {
    label: 'Cancels',
    linkTo: createUrl(AuthorizedPaths.trendsCancels),
    id: 'trends cancels',
    show: deflectLicense,
  } satisfies BBKNavbarOpt;
  const trendsSavesNavOpt = {
    tooltip: isStarterFreePlan
      ? strings.UPGRADE_PLAN({
          description: strings.UPGRADE_TO_STARTER_PREMIUM_SAVES_DESC,
        })
      : undefined,
    label: 'Saves',
    trailingIcon: isStarterFreePlan ? NavIcons.lock : undefined,
    linkTo: createUrl(AuthorizedPaths.trendsSaves),
    disabled: isStarterFreePlan,
    id: 'trends saves',
    show: deflectLicense,
  } satisfies BBKNavbarOpt;
  const customersNavOpt = {
    label: 'Engaged subscribers',
    highlightBg: false,
    leadingIcon: NavIcons.user,
    trailingIcon: expired ? NavIcons.lock : undefined,
    linkTo: createUrl(AuthorizedPaths.customersAll),
    onClick: (e) => {
      const nextCustomersState = !customersExpanded;
      setCustomersExpanded(nextCustomersState);
      if (!nextCustomersState) e.preventDefault();
    },
    show: deflectLicense,
    expandChildren: customersExpanded,
    id: 'customers',
  } satisfies BBKNavbarOpt;
  const customersAllNavOpt = {
    label: 'All',
    linkTo: createUrl(AuthorizedPaths.customersAll),
    id: 'all customers',
    show: deflectLicense,
  } satisfies BBKNavbarOpt;
  const customersCancelsNavOpt = {
    label: 'Cancels',
    linkTo: createUrl(AuthorizedPaths.customersCancels),
    id: 'cancel customers',
    show: deflectLicense,
  } satisfies BBKNavbarOpt;
  const customersSavesNavOpt = {
    tooltip: isStarterFreePlan
      ? strings.UPGRADE_PLAN({
          description: strings.UPGRADE_TO_STARTER_PREMIUM_SAVES_DESC,
        })
      : undefined,
    label: 'Saves',
    trailingIcon: isStarterFreePlan ? NavIcons.lock : undefined,
    linkTo: createUrl(AuthorizedPaths.customersSaves),
    disabled: isStarterFreePlan,
    id: 'save customers',
    show: deflectLicense,
  } satisfies BBKNavbarOpt;
  const customersWatchlistNavOpt = {
    label: 'Watch list',
    linkTo: createUrl(AuthorizedPaths.customersWatchlist),
    id: 'watchlist customers',
    show: deflectLicense,
  } satisfies BBKNavbarOpt;
  const customersLostDeflectsNavOpt = {
    label: 'Lost Deflects',
    linkTo: createUrl(AuthorizedPaths.customersLostDeflects),
    linkAttributes: {
      title: 'Cancels that had a deflect.',
    },
    id: 'lost deflect customers',
    show: deflectLicense,
  } satisfies BBKNavbarOpt;
  const customersRepurchasesNavOpt = {
    tooltip: isStarter
      ? strings.UPGRADE_PLAN({
          description: strings.UPGRADE_TO_DEFLECTION_DESC,
        })
      : undefined,
    label: 'Repurchases',
    trailingIcon: isStarter ? NavIcons.lock : undefined,
    linkTo: createUrl(AuthorizedPaths.customersRepurchases),
    disabled: isStarter,
    show: deflectLicense && Boolean(isRepurchaseAttributionEnabled),
    id: 'repurchase customers',
  } satisfies BBKNavbarOpt;
  const customersWinbacksNavOpt = {
    label: 'Winbacks',
    tooltip: isStarter
      ? strings.UPGRADE_PLAN({
          description: strings.UPGRADE_TO_DEFLECTION_DESC,
        })
      : undefined,
    trailingIcon: isStarter ? NavIcons.lock : undefined,
    linkTo: createUrl(AuthorizedPaths.customersWinbacks),
    disabled: isStarter,
    id: 'winback customers',
    show: deflectLicense,
  } satisfies BBKNavbarOpt;
  const settingsNavOpt = {
    label: 'Settings',
    id: 'settings',
    leadingIcon: NavIcons.settings,
    trailingIcon: settingsMenuBadge,
    linkTo: createUrl(AuthorizedPaths.settings),
  } satisfies BBKNavbarOpt;
  const experiencesNavOpt = {
    id: 'experiences',
    label: 'Experiences',
    leadingIcon: NavIcons.accountTree,
    linkTo: additionalBrandsEnabled
      ? createUrl(AuthorizedPaths.additionalBrandsPage)
      : createUrl(AuthorizedPaths.experiencesBrandingPage),
    onClick: (e) => {
      const newVal = !experiencesExpanded;
      setExperiencesExpanded(newVal);
      if (!newVal) e.preventDefault();
    },
    highlightBg: false,
    disabled: isExpBrandingLibDisabled,
    tooltip: isExpBrandingLibDisabled
      ? 'Create your first cancel experience on the get started page to view this section.'
      : undefined,
    expandChildren: experiencesExpanded,
  } satisfies BBKNavbarOpt;
  const insightsReportsNavOpt = {
    ...insightsNavOpt,
    subItems: [
      insightsCancelAIReportNavOpt,
      insightsCancelsNavOpt,
      insightsOfferPerfNavOpt,
      insightsExpPerfNavOpt,
      insightsRetainRevenueNavOpt,
      insightsLiftReportNavOpt,
    ],
  } satisfies BBKNavbarOpt;
  const trendsReportsNavOpt = {
    ...trendsNavOpt,
    subItems: [
      trendsSavePerfNavOpt,
      trendsDeflectsNavOpt,
      trendsCancelsNavOpt,
      trendsSavesNavOpt,
    ],
  } satisfies BBKNavbarOpt;
  const customersReportsNavOpt = {
    ...customersNavOpt,
    subItems: [
      customersAllNavOpt,
      customersCancelsNavOpt,
      customersSavesNavOpt,
      customersWatchlistNavOpt,
      customersLostDeflectsNavOpt,
      customersRepurchasesNavOpt,
      customersWinbacksNavOpt,
    ],
  } satisfies BBKNavbarOpt;
  const reportsNavOpts: BBKNavbarOpt[] = [
    insightsReportsNavOpt,
    trendsReportsNavOpt,
    customersReportsNavOpt,
  ];
  const agsNavbarOptions: BBKNavbarOpt[] = [
    getStartedNavOpt,
    dashboardNavOpt,
    playsNavOpt,
    offerNavOpt,
    {
      ...experiencesNavOpt,
      subItems: [
        brandingNavOpt,
        brandsNavOpt,
        pagesNavOpt,
        pricingPagesNavOpt,
        targetingNavOpt,
        laNavOpt,
        surveyReasonNavOpt,
        redirectPagesNavOpt,
      ],
    },
    audiencesNavOpt,
    pricingOptimizerNavOpt,
    {
      ...reportsNavOpt,
      subItems: reportsNavOpts,
    },
    settingsNavOpt,
  ];

  const legacyNavbarOptions: BBKNavbarOpt[] = [
    getStartedNavOpt,
    { ...dashboardNavOpt, leadingIcon: icons.gauge },
    { ...playsNavOpt, leadingIcon: icons.play },
    {
      ...experiencesNavOpt,
      leadingIcon: NavIcons.experiences,
      highlightBg: true,
      subItems: [
        { id: 'manage', label: 'Manage', type: 'header' as const },
        { ...brandsNavOpt, icon: NavIcons.pages },
        { ...brandingNavOpt, icon: NavIcons.pages },
        { ...pagesNavOpt, icon: NavIcons.pages },
        { ...pricingPagesNavOpt, icon: NavIcons.pricingActions },
        { ...targetingNavOpt, icon: NavIcons.bullseye },
        { id: 'libraries', label: 'Libraries', type: 'header' as const },
        { ...offerNavOpt, icon: NavIcons.offers },
        { ...laNavOpt, icon: NavIcons.lossAversions },
        { ...surveyReasonNavOpt, icon: NavIcons.reasons },
        { ...redirectPagesNavOpt, icon: NavIcons.confirmationPages },
      ].map((x) => ({ ...x, highlightBg: false })),
    },
    { ...audiencesNavOpt, leadingIcon: NavIcons.audiences },
    pricingOptimizerNavOpt,
    {
      ...insightsReportsNavOpt,
      leadingIcon: icons.insights,
      highlightBg: true,
      subItems: insightsReportsNavOpt.subItems.map((x) => ({
        ...x,
        highlightBg: false,
      })),
    },
    {
      ...trendsReportsNavOpt,
      leadingIcon: icons.trends,
      highlightBg: true,
      subItems: trendsReportsNavOpt.subItems.map((x) => ({
        ...x,
        highlightBg: false,
      })),
    },
    {
      ...customersReportsNavOpt,
      leadingIcon: icons.customers,
      highlightBg: true,
      label: 'Customers',
      subItems: customersReportsNavOpt.subItems.map((x) => ({
        ...x,
        highlightBg: false,
      })),
    },
    settingsNavOpt,
  ];

  const cbNavbarOptions = [
    { ...getStartedNavOpt, label: 'Get started', leadingIcon: 'rocket' },
    { ...dashboardNavOpt, leadingIcon: 'tv-minimal-play' },
    { ...playsNavOpt, leadingIcon: 'play' },
    {
      ...offerGroupNavOpt,
      leadingIcon: 'gift',
      subItems: [
        {
          ...offerNavOpt,
          label: 'In-app',
          leadingIcon: 'monitor',
          trailingIcon: isStarter ? 'lock' : undefined,
        },
        {
          ...pricingPagesNavOpt,
          label: 'Pricing Tables',
          leadingIcon: 'circle-dollar-sign',
          linkTo: undefined,
          tooltip: 'Coming soon...',
          disabled: true,
          // @ts-ignore
          show: true,
        },
      ],
    },
    {
      ...experiencesNavOpt,
      leadingIcon: 'git-fork',
      onClick: undefined,
      subItems: [
        { ...brandingNavOpt, leadingIcon: 'swatch-book' },
        { ...brandsNavOpt, leadingIcon: 'book-image' },
        { ...pagesNavOpt, leadingIcon: 'files' },
        { ...pricingPagesNavOpt, leadingIcon: 'chart-candlestick' },
        {
          ...targetingNavOpt,
          leadingIcon: 'target',
          trailingIcon: isStarter || expired ? 'lock' : undefined,
        },
        {
          ...laNavOpt,
          leadingIcon: 'sticky-note',
          trailingIcon: isStarter ? 'lock' : undefined,
        },
        { ...surveyReasonNavOpt, leadingIcon: 'list-checks' },
        { ...redirectPagesNavOpt, leadingIcon: 'redo-dot' },
      ],
    },
    {
      ...peopleNavOpt,
      leadingIcon: 'user',
      subItems: [
        {
          ...audiencesNavOpt,
          leadingIcon: 'users',
          trailingIcon: isStarter || expired ? 'lock' : undefined,
        },
        {
          ...customersNavOpt,
          leadingIcon: 'user-check',
          trailingIcon: expired ? 'lock' : undefined,
          onClick: undefined,
          subItems: [
            customersAllNavOpt,
            customersCancelsNavOpt,
            {
              ...customersSavesNavOpt,
              trailingIcon: isStarterFreePlan ? 'lock' : undefined,
            },
            customersWatchlistNavOpt,
            customersLostDeflectsNavOpt,
            {
              ...customersRepurchasesNavOpt,
              trailingIcon: isStarter ? 'lock' : undefined,
            },
            {
              ...customersWinbacksNavOpt,
              trailingIcon: isStarter ? 'lock' : undefined,
            },
          ],
        },
      ],
    },
    {
      ...pricingOptimizerNavOpt,
      leadingIcon: 'chart-candlestick',
    },
    {
      ...reportsNavOpt,
      leadingIcon: 'chart-line',
      onClick: undefined,
      subItems: [
        {
          ...insightsNavOpt,
          leadingIcon: 'lightbulb',
          trailingIcon: expired ? 'lock' : undefined,
          onClick: undefined,
          subItems: [
            insightsCancelAIReportNavOpt,
            insightsCancelsNavOpt,
            {
              ...insightsOfferPerfNavOpt,
              trailingIcon: isStarter ? 'lock' : undefined,
            },
            {
              ...insightsExpPerfNavOpt,
              trailingIcon: isStarter ? 'lock' : undefined,
            },
            {
              ...insightsRetainRevenueNavOpt,
              trailingIcon: isStarter ? 'lock' : undefined,
            },
            {
              ...insightsLiftReportNavOpt,
              trailingIcon: isStarter ? 'lock' : undefined,
            },
          ],
        },
        {
          ...trendsNavOpt,
          leadingIcon: 'trending-up',
          trailingIcon: expired ? 'lock' : undefined,
          onClick: undefined,
          subItems: [
            trendsSavePerfNavOpt,
            {
              ...trendsDeflectsNavOpt,
              trailingIcon: isStarter ? 'lock' : undefined,
            },
            trendsCancelsNavOpt,
            {
              ...trendsSavesNavOpt,
              trailingIcon: isStarterFreePlan ? 'lock' : undefined,
            },
          ],
        },
      ],
    },
    {
      ...settingsNavOpt,
      leadingIcon: 'settings',
      trailingIcon: showSettingsBadge ? 'info' : undefined,
    },
  ] satisfies CBNavItem[];

  const touraNavOpt = {
    id: 'tour',
    show: deflectLicense && !isStarter,
    label: 'Tour',
    leadingIcon: icons.flag,
    onClick: () =>
      dispatch(
        appNotPersistedSlice.actions.saveMisc({
          showProductTour: true,
        })
      ),
  } satisfies BBKNavbarOpt;
  const helpDocsNavOpt = {
    id: 'help docs',
    label: 'Help docs',
    leadingIcon: NavIcons.reference,
    linkTo: 'https://www.chargebee.com/docs/retention',
  } satisfies BBKNavbarOpt;
  const contactNavOpt = {
    id: 'contact support',
    label: 'Contact support',
    leadingIcon: icons.contactSupport,
    linkTo: 'mailto:retention-support@chargebee.com',
  };
  const footerNavbarOptions: BBKNavbarOpt[] = [
    touraNavOpt,
    helpDocsNavOpt,
    contactNavOpt,
  ];

  const cbHelpNavbarOptions: CBNavItem[] = [
    {
      ...helpDocsNavOpt,
      leadingIcon: 'file-question-mark',
      label: 'Help Documentation',
    },
    {
      id: 'api reference',
      label: 'API Reference',
      linkTo:
        'https://www.chargebee.com/docs/retention/chargebee-billing-integration/how-to-setup-chargebee-retention-for-the-chargebee-retention-api',
      leadingIcon: 'terminal',
    },
    { ...touraNavOpt, leadingIcon: 'flag' },
    { ...contactNavOpt, leadingIcon: 'send' },
  ];

  const purchaseNavbarOptions = [
    alertText
      ? ({
          id: 'purchase',
          label: alertText,
          ...(hasBillingRole
            ? {
                linkTo: Constants.PurchaseLink,
                onClick: () => handlePlanPurchaseIntent(),
                badge: { label: 'Purchase', variant: 'primary' },
              }
            : {}),
        } satisfies CBNavItem)
      : undefined,
  ].filter(isDefined) satisfies CBNavItem[];

  return {
    agsNavbarOptions: NavBarHelper.filterNavbarOptions(agsNavbarOptions),
    legacyNavbarOptions: NavBarHelper.filterNavbarOptions(legacyNavbarOptions),
    cbNavbarOptions: NavBarHelper.filterNavbarOptions(cbNavbarOptions),
    internalNavbarOptions: NavBarHelper.filterNavbarOptions(
      internalNavbarOptions
    ),
    helpNavbarOptions: NavBarHelper.filterNavbarOptions(footerNavbarOptions),
    cbHelpNavbarOptions: NavBarHelper.filterNavbarOptions(cbHelpNavbarOptions),
    purchaseNavbarOptions: NavBarHelper.filterNavbarOptions(
      purchaseNavbarOptions
    ),
  };
};
